// Réglages de la pilule (parcours « Elle », étape 4) : une fois, modifiables
// ensuite. Chaque valeur non confirmée a un défaut côté serveur (19 h 30, 21/7,
// H+30, « Rappel du soir », pilule inconnue) ; rien n'est figé ici.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type SubmitEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Bouton, Chargement, Erreur, ErreurReseau, Texte, Titre } from '../components/base';
import { Case, Champ, Choix } from '../components/champs';
import { api, type Famille, type Reglages } from '../lib/api';
import { fuseauAppareil } from '../lib/format';
import { enregistrerNomPilule, nomPilule } from '../lib/jeton';
import { useMoi, useRegles } from '../lib/requetes';
import { type Formulaire, initial, type Maj, versServeur } from './reglages-formulaire';
import { ChampsAvances } from './ReglagesAvances';

function ChampDebut({ f, maj }: Readonly<{ f: Formulaire; maj: Maj }>) {
  const { t } = useTranslation();
  return (
    <>
      <Choix
        legende={t('reglages.debutTitre')}
        nom="modeDebut"
        options={[
          { valeur: 'rang', libelle: t('reglages.debutRang') },
          { valeur: 'date', libelle: t('reglages.debutDate') },
        ]}
        valeur={f.modeDebut}
        onChange={(modeDebut) => maj({ modeDebut })}
      />
      {f.modeDebut === 'rang' ? (
        <Champ
          id="rang"
          libelle={t('reglages.rang')}
          type="number"
          min={1}
          max={28}
          inputMode="numeric"
          value={f.rang}
          onChange={(e) => maj({ rang: Number(e.target.value) })}
          className="w-24"
        />
      ) : (
        <Champ
          id="debut"
          libelle={t('reglages.debut')}
          type="date"
          required
          value={f.debutPlaquette ?? ''}
          onChange={(e) => maj({ debutPlaquette: e.target.value })}
        />
      )}
    </>
  );
}

function ChampsPlaquette({ f, maj }: Readonly<{ f: Formulaire; maj: Maj }>) {
  const { t } = useTranslation();
  const schemas = (['21/7', '24/4', 'continu'] as const).map((v) => ({
    valeur: v,
    libelle: t(`reglages.schema.${v}`),
  }));
  const inactifs = (['arret', 'placebo'] as const).map((v) => ({
    valeur: v,
    libelle: t(`reglages.inactifs.${v}`),
  }));
  return (
    <>
      <Choix
        legende={t('reglages.schemaTitre')}
        nom="schema"
        options={schemas}
        valeur={f.schema}
        onChange={(schema) => maj({ schema })}
      />
      {f.schema !== 'continu' && (
        <Choix
          legende={t('reglages.inactifsTitre')}
          nom="inactifs"
          options={inactifs}
          valeur={f.inactifs}
          onChange={(v) => maj({ inactifs: v })}
        />
      )}
      <ChampDebut f={f} maj={maj} />
    </>
  );
}

function ChampsPilule({ f, maj }: Readonly<{ f: Formulaire; maj: Maj }>) {
  const { t } = useTranslation();
  const { data } = useRegles();
  const familles = (data?.regles ?? []).map((r) => ({ valeur: r.famille, libelle: r.libelle }));
  return (
    <>
      <Champ
        id="nom"
        libelle={t('reglages.nom')}
        aide={t('reglages.nomAide')}
        type="text"
        autoComplete="off"
        maxLength={60}
        value={f.nom}
        onChange={(e) => maj({ nom: e.target.value })}
        className="w-full"
      />
      {familles.length > 0 && (
        <Choix<Famille>
          legende={t('reglages.familleTitre')}
          aide={t('reglages.familleAide')}
          nom="famille"
          options={familles}
          valeur={f.famille}
          onChange={(famille) => maj({ famille })}
        />
      )}
    </>
  );
}

/** Le nom de la pilule est gardé ici seulement ; le reste part au serveur. */
function useEnregistrer(f: Formulaire, onEnregistre?: () => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.reglages(versServeur(f, fuseauAppareil())),
    onSuccess: async () => {
      enregistrerNomPilule(f.nom);
      await client.invalidateQueries();
      onEnregistre?.();
    },
  });
}

function FormReglages({
  depart,
  onEnregistre,
}: Readonly<{ depart: Reglages; onEnregistre?: () => void }>) {
  const { t } = useTranslation();
  const [f, setF] = useState(() => initial(depart, nomPilule()));
  const maj: Maj = (p) => setF((avant) => ({ ...avant, ...p }));
  const enregistrer = useEnregistrer(f, onEnregistre);
  const envoyer = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    enregistrer.mutate();
  };
  return (
    <form onSubmit={envoyer}>
      <Champ
        id="heure"
        libelle={t('reglages.heure')}
        aide={t('reglages.heureAide')}
        type="time"
        required
        value={f.heure}
        onChange={(e) => maj({ heure: e.target.value })}
      />
      <ChampsPlaquette f={f} maj={maj} />
      <ChampsPilule f={f} maj={maj} />
      <Case
        id="prevenir"
        libelle={t('reglages.prevenir')}
        aide={t('reglages.prevenirAide')}
        coche={f.prevenirProche}
        onChange={(prevenirProche) => maj({ prevenirProche })}
      />
      <ChampsAvances f={f} maj={maj} />
      <Bouton type="submit" className="mt-10" disabled={enregistrer.isPending}>
        {t('reglages.enregistrer')}
      </Bouton>
      {enregistrer.isSuccess && !onEnregistre && (
        <p role="status" className="mt-3">
          {t('reglages.enregistre')}
        </p>
      )}
      {enregistrer.isError && <Erreur>{t('reglages.erreur')}</Erreur>}
    </form>
  );
}

export function EcranReglages({
  premiereFois = false,
  onEnregistre,
}: Readonly<{
  premiereFois?: boolean;
  onEnregistre?: () => void;
}>) {
  const { t } = useTranslation();
  const { data: moi, isError, refetch } = useMoi();
  if (isError) return <ErreurReseau onReessayer={() => void refetch()} />;
  if (!moi) return <Chargement />;
  if (moi.role !== 'elle' || !('schema' in moi.reglages))
    return <Texte>{t('reglages.reserve')}</Texte>;
  return (
    <section>
      <Titre>{t(premiereFois ? 'reglages.titrePremiere' : 'reglages.titre')}</Titre>
      {premiereFois && <Texte discret>{t('reglages.introPremiere')}</Texte>}
      <FormReglages depart={moi.reglages} onEnregistre={onEnregistre} />
    </section>
  );
}
